import { useState, useCallback, useRef } from 'react';

/**
 * GPS 定位 hook - 含重試與降級機制
 * 
 * 定位策略：
 * 1. 嘗試高精度 GPS（timeout: 15s）
 * 2. 若失敗，等待 2 秒後重試高精度 GPS
 * 3. 若再失敗，降級為低精度 WiFi/基地台定位（timeout: 15s）
 * 4. 若全部失敗，回傳錯誤
 * 
 * @param {Object} options - geolocation.getCurrentPosition 選項
 * @returns {Object} { position, error, isLoading, positionStatus,
 *                     getPosition, getPositionPromise, getPositionWithRetry }
 */
export function useGeolocation(options = {}) {
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [positionStatus, setPositionStatus] = useState('idle'); // idle | locating | low_accuracy | success | failed
  const resolveRef = useRef(null);
  const rejectRef = useRef(null);

  // 核心定位函數：執行一次 navigator.geolocation.getCurrentPosition
  const executePositionRequest = useCallback((highAccuracy = true, timeout = 15000) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('您的瀏覽器不支援 GPS 定位'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newPos = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
          resolve(newPos);
        },
        (err) => {
          const errMsg = getGeolocationErrorMessage(err.code);
          reject(new Error(errMsg));
        },
        {
          enableHighAccuracy: highAccuracy,
          timeout,
          maximumAge: 0,
          ...options
        }
      );
    });
  }, [options]);

  // 重試 + 降級定位（主要用於打卡流程）
  const getPositionWithRetry = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('您的瀏覽器不支援 GPS 定位');
      setPositionStatus('failed');
      return null;
    }

    setIsLoading(true);
    setError(null);
    setPositionStatus('locating');

    // 嘗試 1: 高精度 GPS
    try {
      const pos = await executePositionRequest(true, 15000);
      setPosition(pos);
      setPositionStatus('success');
      setIsLoading(false);
      return pos;
    } catch (err1) {
      console.warn('GPS 高精度定位嘗試 1 失敗:', err1.message);
    }

    // 等待 2 秒後重試
    await sleep(2000);

    // 嘗試 2: 高精度 GPS（重試）
    try {
      const pos = await executePositionRequest(true, 15000);
      setPosition(pos);
      setPositionStatus('success');
      setIsLoading(false);
      return pos;
    } catch (err2) {
      console.warn('GPS 高精度定位嘗試 2 失敗:', err2.message);
    }

    // 等待 2 秒後降級
    await sleep(2000);

    // 嘗試 3: 降級為低精度（WiFi/基地台定位）
    try {
      const pos = await executePositionRequest(false, 15000);
      setPosition(pos);
      setPositionStatus('low_accuracy');
      setIsLoading(false);
      return pos;
    } catch (err3) {
      const finalError = '定位失敗：已嘗試 GPS 與基地台定位，請移至窗邊或室外再試';
      setError(finalError);
      setPositionStatus('failed');
      setIsLoading(false);
      return null;
    }
  }, [executePositionRequest]);

  // 保留原始 getPosition（單次高精度定位，用於手動獲取位置按鈕）
  const getPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setError('您的瀏覽器不支援 GPS 定位');
      return;
    }

    setIsLoading(true);
    setError(null);
    setPositionStatus('locating');

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        };
        setPosition(newPos);
        setPositionStatus('success');
        setIsLoading(false);
        if (resolveRef.current) {
          resolveRef.current(newPos);
          resolveRef.current = null;
          rejectRef.current = null;
        }
      },
      (err) => {
        const errMsg = getGeolocationErrorMessage(err.code);
        setError(errMsg);
        setPositionStatus('failed');
        setIsLoading(false);
        if (rejectRef.current) {
          rejectRef.current(new Error(errMsg));
          resolveRef.current = null;
          rejectRef.current = null;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
        ...options
      }
    );
  }, [options]);

  // 保留原始 getPositionPromise（單次高精度定位 Promise 版本）
  const getPositionPromise = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('您的瀏覽器不支援 GPS 定位'));
        return;
      }

      setIsLoading(true);
      setError(null);
      setPositionStatus('locating');

      resolveRef.current = resolve;
      rejectRef.current = reject;

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newPos = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
          setPosition(newPos);
          setPositionStatus('success');
          setIsLoading(false);
          resolve(newPos);
        },
        (err) => {
          const errMsg = getGeolocationErrorMessage(err.code);
          setError(errMsg);
          setPositionStatus('failed');
          setIsLoading(false);
          reject(new Error(errMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
          ...options
        }
      );
    });
  }, [options]);

  return {
    position,
    error,
    isLoading,
    positionStatus,
    getPosition,
    getPositionPromise,
    getPositionWithRetry
  };
}

// 延遲工具函數
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// 翻譯 geolocation 錯誤碼為中文訊息
function getGeolocationErrorMessage(code) {
  const messages = {
    1: '您已拒絕提供位置資訊，請允許存取以進行打卡',
    2: '無法取得您的位置，請檢查 GPS 訊號',
    3: '定位逾時，請重試',
    4: '未知錯誤，請重試'
  };
  return messages[code] || `定位失敗 (錯誤碼: ${code})`;
}